/**
 * iOS Safari는 소프트 키보드가 열려도 layout viewport(`window.innerHeight`)를 줄이지 않는다.
 * 대신 visual viewport의 높이만 줄이고, 포커스된 요소를 노출하기 위해 visual viewport를
 * layout viewport 안에서 아래로 밀어낸다(`visualViewport.offsetTop`).
 *
 * `position: fixed` / `sticky`는 layout viewport 기준으로 배치되므로 offsetTop 만큼
 * 화면 밖(위쪽)으로 밀려나 보인다. 이 모듈은 그 보정값을 CSS 변수로 노출한다.
 *
 * - `--keyboard-inset`            키보드가 가리는 높이. fixed 요소의 `bottom` 보정값이며,
 *                                 offsetTop 보정까지 포함한다(아래 계산식 참고).
 * - `--visual-viewport-height`    실제로 보이는 영역의 높이
 * - `--visual-viewport-offset-top` visual viewport가 밀린 거리. 상단 고정 요소의 보정값
 * - `--keyboard-open`             `0` | `1` (단위 없는 수. calc 곱셈용)
 *
 * `bottom: var(--keyboard-inset)` 하나로 키보드 회피와 offsetTop 보정이 동시에 되는 이유:
 * fixed 요소의 하단은 layout 좌표로 `innerHeight - bottom`에 놓이고, 키보드 상단은
 * `offsetTop + visualViewport.height`이므로 둘을 같게 두면
 * `bottom = innerHeight - visualViewport.height - offsetTop`이 된다.
 */

export interface VisualViewportInsets {
  /** 키보드가 가리는 높이 (px) */
  keyboardInset: number;
  /** 현재 보이는 영역의 높이 (px) */
  viewportHeight: number;
  /** visual viewport가 layout viewport 안에서 밀린 거리 (px) */
  offsetTop: number;
}

/**
 * 이모지/예측 입력줄 토글 같은 미세 변화를 키보드 개폐로 오인하지 않기 위한 임계값.
 * vaul도 동일하게 60px를 쓴다.
 */
export const KEYBOARD_OPEN_THRESHOLD = 60;

const INITIAL_INSETS: VisualViewportInsets = {
  keyboardInset: 0,
  viewportHeight: 0,
  offsetTop: 0,
};

let insets: VisualViewportInsets = INITIAL_INSETS;
let started = false;
let frame: number | null = null;
let settleTimer: ReturnType<typeof setTimeout> | null = null;

const listeners = new Set<() => void>();

/** `computeInsets`의 입력. 브라우저 API를 분리해 테스트 가능하게 한다. */
export interface ViewportMetrics {
  /** layout viewport 높이 (`window.innerHeight`) */
  innerHeight: number;
  viewportHeight: number;
  viewportOffsetTop: number;
  /** `visualViewport.scale`. 핀치 줌 배율 */
  scale: number;
}

export function computeInsets(metrics: ViewportMetrics): VisualViewportInsets {
  // 핀치 줌 중에는 확대 때문에 height가 줄고 offsetTop이 생긴다.
  // 그대로 계산하면 키보드가 열린 것으로 오인한다.
  if (metrics.scale > 1) {
    return {
      keyboardInset: 0,
      viewportHeight: Math.round(metrics.viewportHeight),
      offsetTop: 0,
    };
  }

  return {
    keyboardInset: Math.max(
      0,
      Math.round(
        metrics.innerHeight -
          metrics.viewportHeight -
          metrics.viewportOffsetTop,
      ),
    ),
    viewportHeight: Math.round(metrics.viewportHeight),
    offsetTop: Math.round(metrics.viewportOffsetTop),
  };
}

function read(): VisualViewportInsets {
  const viewport = window.visualViewport;

  if (!viewport) {
    return {
      keyboardInset: 0,
      viewportHeight: window.innerHeight,
      offsetTop: 0,
    };
  }

  return computeInsets({
    innerHeight: window.innerHeight,
    viewportHeight: viewport.height,
    viewportOffsetTop: viewport.offsetTop,
    scale: viewport.scale,
  });
}

function apply(next: VisualViewportInsets) {
  const root = document.documentElement;
  root.style.setProperty("--keyboard-inset", `${next.keyboardInset}px`);
  root.style.setProperty(
    "--visual-viewport-height",
    `${next.viewportHeight}px`,
  );
  root.style.setProperty("--visual-viewport-offset-top", `${next.offsetTop}px`);
  root.style.setProperty(
    "--keyboard-open",
    next.keyboardInset > KEYBOARD_OPEN_THRESHOLD ? "1" : "0",
  );
}

function sync() {
  frame = null;

  const next = read();
  if (
    next.keyboardInset === insets.keyboardInset &&
    next.viewportHeight === insets.viewportHeight &&
    next.offsetTop === insets.offsetTop
  ) {
    return;
  }

  insets = next;
  apply(next);
  listeners.forEach((listener) => listener());
}

function schedule() {
  if (frame != null) return;
  frame = requestAnimationFrame(sync);
}

/**
 * iOS는 키보드 애니메이션 중 값을 두 번에 나눠 보고하는 경우가 있어
 * 이벤트 직후 한 번 더 확인한다.
 */
function scheduleWithSettle() {
  schedule();
  if (settleTimer != null) clearTimeout(settleTimer);
  settleTimer = setTimeout(() => {
    settleTimer = null;
    schedule();
  }, 300);
}

/** 앱 시작 시 한 번만 호출한다. */
export function startVisualViewportTracking() {
  if (started || typeof window === "undefined") return;
  started = true;

  insets = read();
  apply(insets);

  const viewport = window.visualViewport;
  if (viewport) {
    viewport.addEventListener("resize", scheduleWithSettle);
    viewport.addEventListener("scroll", schedule);
  }
  window.addEventListener("resize", scheduleWithSettle);
  window.addEventListener("orientationchange", scheduleWithSettle);
}

export function getVisualViewportInsets(): VisualViewportInsets {
  return insets;
}

export function isKeyboardOpen(): boolean {
  return insets.keyboardInset > KEYBOARD_OPEN_THRESHOLD;
}

export function subscribeVisualViewportInsets(
  listener: () => void,
): () => void {
  startVisualViewportTracking();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 보이는 영역의 하단 y좌표(layout viewport 기준). 키보드가 열려 있으면 키보드 상단이다.
 * `getBoundingClientRect()`가 layout viewport 기준 좌표를 주므로 그대로 비교할 수 있다.
 */
export function getVisibleBottom(): number {
  const viewport = window.visualViewport;
  if (!viewport) return window.innerHeight;
  return viewport.offsetTop + viewport.height;
}

interface WaitForLayoutSettleOptions {
  /** 지금부터 뷰포트가 변할 것으로 기대하는지 (키보드가 새로 열리는 경우) */
  expectChange: boolean;
  /** 함께 안정되기를 기다릴 요소. 바텀시트 열림 애니메이션이 끝날 때까지 기다리는 용도 */
  element?: HTMLElement | null;
  /**
   * 폴링을 중단할 조건. 폼에서 필드를 빠르게 옮겨 다니면 폴링 루프가 겹쳐 쌓이므로,
   * 더 이상 쓸모없어진 루프는 즉시 멈춘다.
   */
  isCancelled?: () => boolean;
  timeoutMs?: number;
}

/**
 * visual viewport(와 선택한 요소의 위치)가 안정될 때까지 기다린다.
 *
 * iOS는 키보드 애니메이션 도중 값을 늦게/드문드문 갱신하므로,
 * resize 이벤트 하나만 믿으면 아직 옛 값으로 위치를 계산하게 된다.
 * rAF로 값을 폴링해 (1) 변화가 시작되고 (2) 3프레임 연속 같은 값이면 안정된 것으로 본다.
 */
export function waitForLayoutSettle({
  expectChange,
  element,
  isCancelled,
  timeoutMs = 900,
}: WaitForLayoutSettleOptions): Promise<void> {
  return new Promise((resolve) => {
    const viewport = window.visualViewport;
    if (!viewport) {
      resolve();
      return;
    }

    // height와 offsetTop은 iOS에서 서로 반대 방향으로 움직이므로 합치면 안 된다.
    // 합이 우연히 유지되는 프레임을 "안정"으로 오판할 수 있다.
    const measure = () => {
      const elementTop = element
        ? Math.round(element.getBoundingClientRect().top)
        : 0;
      return `${Math.round(viewport.height)}:${Math.round(viewport.offsetTop)}:${elementTop}`;
    };
    const startedAt = performance.now();
    const baseline = measure();

    let changed = !expectChange;
    let stableFrames = 0;
    let previous = baseline;

    const tick = () => {
      if (isCancelled?.()) {
        resolve();
        return;
      }

      const current = measure();

      if (performance.now() - startedAt > timeoutMs) {
        resolve();
        return;
      }

      if (!changed) {
        if (current !== baseline) changed = true;
      } else if (current === previous) {
        stableFrames += 1;
        if (stableFrames >= 3) {
          resolve();
          return;
        }
      } else {
        stableFrames = 0;
      }

      previous = current;
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
}
